import { getAuth, signInWithCustomToken, type User } from "firebase/auth";
import api from "lib/axios";
import type { ApiEnvelope } from "services/apiTypes";
import { getFirebaseApp } from "./firebaseClient";

type FirebaseTokenResponse = {
  firebase_token: string;
  firebase_uid: string;
  expires_in: number;
  claims: {
    role: string;
    app_user_id: number;
    guard: string;
  };
};

export type FirebaseSignInResult = {
  user: User;
  firebaseUid: string;
  appUserId: number;
  role: string;
};

let signInPromise: Promise<FirebaseSignInResult> | null = null;
/** In-memory only (never persisted) cache of the last successful sign-in. */
let cachedSession: FirebaseSignInResult | null = null;

/**
 * Signs into Firebase with a Laravel custom token and returns the verified
 * identity/claims. The result is cached in memory so reopening a chat does not
 * re-request the token or re-run sign-in while the same user is active.
 */
export async function ensureFirebaseSignedIn(): Promise<FirebaseSignInResult> {
  const auth = getAuth(getFirebaseApp());

  // Fast path: already signed in this session — no token round-trip.
  if (cachedSession && auth.currentUser && auth.currentUser.uid === cachedSession.firebaseUid) {
    return cachedSession;
  }

  if (!signInPromise) {
    signInPromise = (async () => {
      const res = await api.get<ApiEnvelope<FirebaseTokenResponse>>("/common/firebase/token", {
        meta: { toastSuccess: false, toastError: false } as any,
      });
      const result = res.data.result;
      let payload: FirebaseTokenResponse | undefined;
      if (result && typeof result === "object" && "data" in result) {
        payload = (result as { data?: FirebaseTokenResponse }).data;
      } else {
        payload = result as FirebaseTokenResponse;
      }
      const token = payload?.firebase_token;
      if (!token) {
        throw new Error("Missing Firebase custom token");
      }

      let user = auth.currentUser;
      if (!user || user.uid !== payload!.firebase_uid) {
        const credential = await signInWithCustomToken(auth, token);
        user = credential.user;
      }

      cachedSession = {
        user,
        firebaseUid: payload!.firebase_uid,
        appUserId: Number(payload!.claims?.app_user_id ?? 0),
        role: String(payload!.claims?.role ?? ""),
      };
      return cachedSession;
    })().finally(() => {
      signInPromise = null;
    });
  }

  return signInPromise;
}

/** Clears the cached Firebase session (call on logout / account switch). */
export function resetFirebaseSession(): void {
  cachedSession = null;
  signInPromise = null;
}

export async function signOutFirebase(): Promise<void> {
  resetFirebaseSession();
  const auth = getAuth(getFirebaseApp());
  if (auth.currentUser) {
    await auth.signOut();
  }
}
